import { useState } from 'react'
import {
  BarChart,
  Bar,
  ComposedChart,
  Line,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useSeriesToggle } from './charts/useSeriesToggle'
import { useAxisAmount } from './charts/useAxisAmount'
import { ChartWrapper } from './ChartWrapper'
import { useFormatters } from '../../contexts/PreferencesContext'
import { useDrilldownAction } from '../drilldown'
import type { DrilldownFilters } from '../drilldown'
import { PPVViewData, PPVVarianceItem, RechartsClickData } from './types'
import { costLabel, nameAxisLayout, nameTick } from './ppvBarLabels'

/**
 * CR-5456: Purchase Price Variance chart panels, replicating the legacy
 * Supplier Portal Cost dashboard. Rendered once per view (Future / Historical)
 * — both views share the same 5 panels, differing only in the underlying
 * quantity grain (computed server-side: outstanding vs received).
 */

// Helix dashboard palette (kept consistent with the rest of the Cost tab).
const COLOR_PPV = '#3b82f6' // PPV £ bars (blue)
const COLOR_PCT = '#93c5fd' // % PPV line (light blue)
const COLOR_COST = '#ef4444' // Positive variance — higher cost (red)
const COLOR_SAVINGS = '#f59e0b' // Negative variance — higher savings (amber)


/**
 * Turn an over-time bucket label into the calendar month the drilldown should
 * filter on. The backend emits `MM-YYYY` (PPVDashboardService.over_time), so
 * this deliberately does not reuse CostTab's `Mon YYYY` parser.
 *
 * @param label - bucket label from the X axis, e.g. `10-2026`
 * @returns drilldown date window for that month, or null if unparseable
 */
function monthLabelToRange(label: string): { date_from: string; date_to: string } | null {
  const match = /^(\d{1,2})-(\d{4})$/.exec(label.trim())
  if (!match) return null
  const month = Number(match[1])
  const year = Number(match[2])
  if (month < 1 || month > 12) return null
  const mm = String(month).padStart(2, '0')
  const lastDay = new Date(year, month, 0).getDate()
  return { date_from: `${year}-${mm}-01`, date_to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}` }
}

/** Top-N selector (matches the legacy "Top 5" dropdown). */
export function TopNSelector({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-xs border rounded px-2 py-1 bg-background"
    >
      <option value={5}>Top 5</option>
      <option value={10}>Top 10</option>
      <option value={20}>Top 20</option>
    </select>
  )
}

/** One horizontal-bar variance panel (by supplier or by part, positive or negative). */
function VariancePanel({
  groupLabel,
  title,
  items,
  color,
  exportFilename,
  entity,
  positive,
  onBarClick,
}: {
  groupLabel: string
  title: string
  items: PPVVarianceItem[]
  color: string
  exportFilename: string
  /** What the bars are grouped by — drives the empty-state wording. */
  entity: 'suppliers' | 'part numbers'
  /** True for the cost-overrun panel, false for the savings panel. */
  positive: boolean
  onBarClick: (name: string | undefined) => void
}) {
  const { formatCurrency } = useFormatters()
  const axisAmount = useAxisAmount() // CR-7120: numbers only on the axis
  const [topN, setTopN] = useState(5)
  const data = items.slice(0, topN)
  const axis = nameAxisLayout(data.map((d) => d.name))

  // A panel legitimately empties out when every row falls on the other side of
  // zero — most often the supplier panels, where each supplier's parts are
  // netted, so a supplier that saves overall never appears under Higher Cost
  // even when individual parts of theirs do. Without this the card rendered as
  // an empty plot area and read as a broken chart.
  if (data.length === 0) {
    return (
      <div>
        <p className="text-sm font-semibold mb-1">{groupLabel}</p>
        <ChartWrapper title={title} exportFilename={exportFilename}>
          <div className="h-[220px] flex flex-col items-center justify-center text-center px-6">
            <p className="text-sm text-muted-foreground">
              No {entity} with a {positive ? 'higher-cost' : 'higher-savings'} variance in this view.
            </p>
            {entity === 'suppliers' && (
              <p className="text-xs text-muted-foreground mt-1">
                Supplier variance nets cost and savings across all of their parts — the Part Number
                panel shows the part-level split.
              </p>
            )}
          </div>
        </ChartWrapper>
      </div>
    )
  }

  return (
    <div>
      <p className="text-sm font-semibold mb-1">{groupLabel}</p>
      <ChartWrapper
        title={title}
        data={data}
        exportFilename={exportFilename}
        headerActions={<TopNSelector value={topN} onChange={setTopN} />}
      >
        <ResponsiveContainer width="100%" height={220}>
          {/* Click-through lives on <Bar> (below), not on the chart: the
              chart-level handler also fires for a bar click, and in this
              horizontal layout its hover-derived activePayload is empty — so
              it re-opened the drilldown a moment later with no supplier /
              part filter at all, wiping the bar's own scope. */}
          <BarChart data={data} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={axisAmount} />
            {/* CR-8612: sized to the names; a name that still does not fit is shortened, full on hover. */}
            <YAxis type="category" dataKey="name" width={axis.width} tick={nameTick(axis.maxChars)} interval={0} />
            <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
            <Bar
              dataKey="variance"
              fill={color}
              name={title}
              radius={[0, 4, 4, 0]}
              cursor="pointer"
              onClick={(raw: unknown) => {
                // Recharts' chart-level onClick only reports a bar via the
                // hover-derived activePayload, which is empty on a direct
                // click in this horizontal layout — the drilldown then opened
                // unfiltered. The Bar handler gets the row itself.
                const row = raw as { name?: unknown; payload?: { name?: unknown } }
                const name = row?.payload?.name ?? row?.name
                onBarClick(typeof name === 'string' ? name : undefined)
              }}
            >
              {/* CR-8612: inside the bar end when it fits, else just outside — never on the names. */}
              <LabelList dataKey="variance" content={costLabel(formatCurrency)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>
    </div>
  )
}

interface PPVChartPanelsProps {
  /** Server-aggregated PPV data for this view. */
  view: PPVViewData
  /** True for the Future view (adds the Arrears bar series to the over-time chart). */
  isFuture: boolean
  /** Dashboard filters mapped to the drilldown API format. */
  drilldownFilters: DrilldownFilters
}

export function PPVChartPanels({ view, isFuture, drilldownFilters }: PPVChartPanelsProps) {
  const series = useSeriesToggle() // CR-1421 item 5: legend show/hide
  const { openDrilldown } = useDrilldownAction()
  const { formatCurrency } = useFormatters()
  const axisAmount = useAxisAmount() // CR-7120: numbers only on the axis

  const hasData =
    view.over_time.length > 0 ||
    view.by_supplier_positive.length > 0 ||
    view.by_supplier_negative.length > 0 ||
    view.by_part_positive.length > 0 ||
    view.by_part_negative.length > 0

  if (!hasData) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 font-medium">Purchase Price Variance (PPV)</p>
        <p className="text-sm text-blue-700 mt-1">
          No contracted PO lines match the current filters. PPV is shown only for lines with a
          matching price-book contract price.
        </p>
      </div>
    )
  }

  // Every PPV drilldown carries the view it was opened from: the backend uses
  // it to pick the same quantity grain and date the bars are drawn at (Future =
  // outstanding qty by promise date, Historical = received qty by ODD, CR-8180),
  // so the modal's PPV column and month reconcile against the chart.
  const viewFilters: DrilldownFilters = {
    ...drilldownFilters,
    ppv_view: isFuture ? 'future' : 'historical',
  }

  const viewLabel = isFuture ? 'Future' : 'Historical'
  const openSupplier = (name: string | undefined) =>
    openDrilldown('ppv', { ...viewFilters, ...(name && { supplier_name: name }) }, name || viewLabel)
  const openPart = (name: string | undefined) =>
    openDrilldown('ppv', { ...viewFilters, ...(name && { part_number: name }) }, name || viewLabel)

  // Over-time chart: scope to the month bucket that was clicked. The
  // supplier / part panels aggregate across all months, so they stay unscoped.
  const openMonth = (chartData: RechartsClickData | undefined) => {
    const label =
      chartData?.activeLabel ??
      (chartData?.activePayload?.[0]?.payload?.['month'] as string | undefined)
    const range = label ? monthLabelToRange(label) : null
    openDrilldown('ppv', range ? { ...viewFilters, ...range } : viewFilters, label || viewLabel)
  }

  return (
    <div className="space-y-6">
      {/* Panel 1 — PPV to Contract/Standard (combo: £ bars + % line) */}
      <ChartWrapper
        title="PPV to Contract/Standard"
        data={view.over_time}
        exportFilename={`ppv-over-time-${isFuture ? 'future' : 'historical'}`}
        legend={[
          { color: COLOR_PPV, label: 'PPV' },
          { color: COLOR_PCT, label: '% PPV' },
        ]}
        visibleSeries={series.visibleSeries}
        onSeriesToggle={series.onSeriesToggle}
      >
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={view.over_time}
            onClick={(chartData) => openMonth(chartData as RechartsClickData)}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="value" tick={{ fontSize: 11 }} tickFormatter={axisAmount} />
            <YAxis
              yAxisId="pct"
              orientation="right"
              tick={{ fontSize: 11 }}
              tickFormatter={(value: number) => `${value}%`}
            />
            <Tooltip
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
              formatter={(value: number, name: string) =>
                name === '% PPV' ? `${value}%` : formatCurrency(value)
              }
            />
            <Bar yAxisId="value" dataKey="ppv" fill={COLOR_PPV} name="PPV" radius={[4, 4, 0, 0]} hide={series.isHidden('PPV')} />
            {/* CR-1421 decision 3: the Future view follows the period, as legacy;
                overdue lines are no longer drawn as an Arrears series. */}
            <Line yAxisId="pct" type="monotone" dataKey="ppv_pct" stroke={COLOR_PCT} strokeWidth={2} name="% PPV" dot={{ r: 3 }}
              hide={series.isHidden('% PPV')} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Panels 2 & 4 — Positive (Higher Cost): by Supplier | by Part */}
      <div className="grid grid-cols-2 gap-6">
        <VariancePanel
          groupLabel="Suppliers:"
          title="PPV Variance (Positive - Higher Cost)"
          items={view.by_supplier_positive}
          color={COLOR_COST}
          exportFilename={`ppv-supplier-cost-${isFuture ? 'future' : 'historical'}`}
          entity="suppliers"
          positive
          onBarClick={openSupplier}
        />
        <VariancePanel
          groupLabel="Part Number:"
          title="PPV Variance (Positive - Higher Cost)"
          items={view.by_part_positive}
          color={COLOR_COST}
          exportFilename={`ppv-part-cost-${isFuture ? 'future' : 'historical'}`}
          entity="part numbers"
          positive
          onBarClick={openPart}
        />
      </div>

      {/* Panels 3 & 5 — Negative (Higher Savings): by Supplier | by Part */}
      <div className="grid grid-cols-2 gap-6">
        <VariancePanel
          groupLabel="Suppliers:"
          title="PPV Variance (Negative - Higher Savings)"
          items={view.by_supplier_negative}
          color={COLOR_SAVINGS}
          exportFilename={`ppv-supplier-savings-${isFuture ? 'future' : 'historical'}`}
          entity="suppliers"
          positive={false}
          onBarClick={openSupplier}
        />
        <VariancePanel
          groupLabel="Part Number:"
          title="PPV Variance (Negative - Higher Savings)"
          items={view.by_part_negative}
          color={COLOR_SAVINGS}
          exportFilename={`ppv-part-savings-${isFuture ? 'future' : 'historical'}`}
          entity="part numbers"
          positive={false}
          onBarClick={openPart}
        />
      </div>
    </div>
  )
}
